import { useContextMenuMESF } from "@dexteel/mesf-core";
import { Paper } from "@mui/material";
import type {
	ColTypeDef,
	DefaultMenuItem,
	GetContextMenuItemsParams,
	MenuItemDef,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useEffect, type MouseEvent as ReactMouseEvent } from "react";

import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";

import { useInspectionOptionsFunctions } from "../hooks/useInspectionOptionsFunctions";
import { useRollingTableData } from "../hooks/useRollingTableData";
import type {
	InspectionWithValues,
	ProfileInspectionsResult,
} from "../../models/Inspection";
import type {
	AverageRow,
	RollingDimensionSummary,
	RollingSummary,
} from "../../models/RollingSummary";
import type { SectionSpecItem } from "@/shared/models/SectionSpec";

const ROLLING_GRID_ROW_HEIGHT = 32;
const ROLLING_GRID_HEADER_HEIGHT = 30;
const VISIBLE_BODY_ROW_COUNT = 5;
const PINNED_ROW_COUNT = 1;
const ROLLING_GRID_HEIGHT =
	ROLLING_GRID_HEADER_HEIGHT +
	ROLLING_GRID_ROW_HEIGHT * (VISIBLE_BODY_ROW_COUNT + PINNED_ROW_COUNT);

interface Props {
	rows: InspectionWithValues[];
	applicableItemsBySpec?: ProfileInspectionsResult["applicableItemsBySpec"];
	currentRollingSectionLabel: string;
	specItems: SectionSpecItem[];
	summary: RollingSummary | null;
	summaryDimensions: RollingDimensionSummary[];
	onNew: () => void;
	onEdit: (row: InspectionWithValues) => void;
	onDelete: (row: InspectionWithValues) => void;
}

export const RollingGrid = ({
	rows,
	applicableItemsBySpec,
	currentRollingSectionLabel,
	specItems,
	summary,
	summaryDimensions,
	onNew,
	onEdit,
	onDelete,
}: Props) => {
	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { getMenuOptions } = useInspectionOptionsFunctions({
		onNew,
		onEdit,
		onDelete,
	});
	const { columnDefs } = useRollingTableData({
		inspections: rows,
		applicableItemsBySpec,
		specItems,
		summaryDimensions,
		onEdit,
		onDelete,
	});
	const { defaultColDef, columnTypes } = useGridDefinitions({});

	const availableSummary = summary && summary.Samples > 0 ? summary : null;
	const pinnedBottom: AverageRow[] = [
		{
			__average: true,
			InspectedAt: "Current Rolling Avg",
			SectionName: currentRollingSectionLabel,
			HeatName: `${availableSummary?.Heats ?? 0} heats`,
			Inspector: `${availableSummary?.Samples ?? 0} samples`,
			MeasuredLbsPerFoot: availableSummary?.AvgLbsPerFoot ?? null,
			values: availableSummary
				? Object.fromEntries(
						summaryDimensions.flatMap((dimension) =>
							dimension.AvgValue === null
								? []
								: [
										[
											dimension.ProfileItemId,
											{ NumericValue: dimension.AvgValue },
										],
									],
						),
					)
				: {},
		},
	];

	const getContextMenuItems = (
		params: GetContextMenuItemsParams<InspectionWithValues | AverageRow>,
	): (DefaultMenuItem | MenuItemDef)[] => {
		const data = params.node?.data;
		if (data && params.event instanceof MouseEvent) {
			showContextMenu(
				params.event as unknown as ReactMouseEvent,
				data,
				"testShackInspectionContext",
			);
			params.api.deselectAll();
			params.node?.setSelected(true);
		}
		return [];
	};

	useEffect(() => {
		registerConfig({
			id: "testShackInspectionContext",
			getOptions: getMenuOptions,
		});
	}, []);

	return (
		<Paper
			elevation={2}
			sx={{
				height: ROLLING_GRID_HEIGHT,
				width: "100%",
				// Column cellStyle callbacks replace the shared flex defaults.
				// Keep renderer spans and plain values centered in these compact rows.
				"& .ag-cell": {
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					lineHeight: "normal",
				},
				"& .ag-center-aligned-cell": {
					textAlign: "center",
				},
				"& .inspection-actions": {
					opacity: 0,
					transition: "opacity 0.1s ease-in-out",
				},
				"& .ag-row-hover .inspection-actions, & .ag-row-focus .inspection-actions, & .ag-row-selected .inspection-actions, & .inspection-actions:focus-within":
					{
						opacity: 1,
					},
				"& .inspection-out-of-band": {
					color: "error.main",
					fontWeight: 600,
				},
			}}
		>
			<AgGridReact<InspectionWithValues | AverageRow>
				loading={false}
				rowData={rows}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				columnTypes={
					columnTypes as Record<
						string,
						ColTypeDef<InspectionWithValues | AverageRow>
					>
				}
				pinnedBottomRowData={pinnedBottom}
				rowHeight={ROLLING_GRID_ROW_HEIGHT}
				headerHeight={ROLLING_GRID_HEADER_HEIGHT}
				animateRows
				getContextMenuItems={getContextMenuItems}
				onRowDoubleClicked={(event) => {
					if (event.data && "ProfileInspectionId" in event.data)
						onEdit(event.data);
				}}
				getRowId={(params) =>
					"ProfileInspectionId" in params.data
						? String(params.data.ProfileInspectionId)
						: "rolling-average"
				}
				gridOptions={{
					rowSelection: {
						mode: "singleRow",
						checkboxes: false,
						enableClickSelection: true,
					},
				}}
			/>
		</Paper>
	);
};
